import React, { useState, useEffect, useRef, useMemo, lazy, Suspense } from 'react';
import { patientApi } from '../../services/api';
const ZoomMeeting = lazy(() => import('./checkinComponents/ZoomMeeting'));
import TicTacToe from './checkinComponents/TicTacToe';
import MemoryMatch from './checkinComponents/MemoryMatch';
import StayRelaxed from './checkinComponents/StayRelaxed';
import WellnessGamesPage from './checkinComponents/WellnessGamesPage';
import ProviderCard from './checkinComponents/ProviderCard';
import { useNavigate } from 'react-router-dom';
import { defaultThemeName } from 'survey-core';
import { useUserData } from '../redux/hooks/useUserData';

const CheckinComplete = ({ tokenData, checkInData, skippedForms = [] }) => {
    const navigate = useNavigate();
    const { appointmentInfo } = useUserData();
    const [showWellnessGames, setShowWellnessGames] = useState(false);
    // Refs
    const hasNotifiedBackend = useRef(false);
    const shouldNotifyBackend = useRef(true);

    // Extract skipped forms from checkInData
    const hasSkippedForms = skippedForms.length > 0;

    // Base64 encoding helper
    const doubleBase64Encode = (str) => {
        if (!str) return '';
        return btoa(btoa(str));
    };

    // Get Zoom meeting info from token data
    const zoomMeetingInfo = useMemo(() => {
        if (!tokenData?.zoom_session) return null;

        return {
            topic: tokenData.zoom_session.topic,
            password: tokenData.zoom_session.passcode,
            userName: tokenData.patient_info ?
                `${tokenData.patient_info.first_name || ''} ${tokenData.patient_info.last_name || ''}`.trim() :
                `${tokenData.first_name || ''} ${tokenData.last_name || ''}`.trim(),
            userEmail: tokenData.patient_info?.email || tokenData.userEmail
        };
    }, [tokenData]);
    
    // Get formatted appointment info from Redux data
    const formattedAppointmentInfo = useMemo(() => {
        if (!appointmentInfo || !appointmentInfo.startDateTime) return null;
        
        let formattedDate = '';
        let formattedTime = '';
        let timezone = '';

        try {
            const appointmentDate = new Date(appointmentInfo.startDateTime);
            const today = new Date();
            const tomorrow = new Date();
            tomorrow.setDate(today.getDate() + 1);

            // Check if appointment is today or tomorrow
            if (appointmentDate.toDateString() === today.toDateString()) {
                formattedDate = 'today';
            } else if (appointmentDate.toDateString() === tomorrow.toDateString()) {
                formattedDate = 'tomorrow';
            } else {
                formattedDate = appointmentDate.toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric'
                });
            }

            formattedTime = appointmentDate.toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            });

            // Get abbreviated timezone automatically
            try {
                const shortTimezone = appointmentDate.toLocaleTimeString('en-US', {
                    timeZoneName: 'short',
                    timeZone: appointmentInfo.timezone
                }).split(' ').pop();
                timezone = shortTimezone || 'CT';
            } catch {
                timezone = 'CT';
            }
        } catch (error) {
            console.error('Error parsing appointment date:', error);
        }

        return {
            ...appointmentInfo,
            formattedDate,
            formattedTime,
            timezone: timezone,
            startDateTime: appointmentInfo.startDateTime,
            appointmentType: appointmentInfo.appointmentType || 'appointment',
            duration: appointmentInfo.minutesDuration || 30
        };
    }, [appointmentInfo]);

    // Calculate estimated wait time
    const waitTimeInfo = useMemo(() => {
        if (!formattedAppointmentInfo?.startDateTime) {
            return null; // Return null when no appointment data available
        }

        try {
            const appointmentTime = new Date(formattedAppointmentInfo.startDateTime);
            const currentTime = new Date();
            const timeDifference = appointmentTime.getTime() - currentTime.getTime();
            const minutesDifference = Math.floor(timeDifference / (1000 * 60));
            const hoursDifference = Math.floor(timeDifference / (1000 * 60 * 60));
            const daysDifference = Math.floor(timeDifference / (1000 * 60 * 60 * 24));

            let estimatedTime = '';
            let message = 'will join shortly';

            if (daysDifference >= 1) {
                const days = Math.min(30, daysDifference);
                estimatedTime = `${days} day${days > 1 ? 's' : ''}`;
                message = 'will join at your scheduled time';
            } else if (hoursDifference >= 1) {
                const hours = Math.min(23, hoursDifference);
                estimatedTime = `${hours} hour${hours > 1 ? 's' : ''}`;
                message = 'will join at your scheduled time';
            } else if (minutesDifference > 15) {
                const minutes = Math.min(59, Math.max(5, minutesDifference - 10));
                estimatedTime = `${minutes} min${minutes > 1 ? 's' : ''}`;
                message = 'will join at your scheduled time';
            } else if (minutesDifference > 0) {
                const minutes = Math.min(59, Math.max(2, minutesDifference));
                estimatedTime = `${minutes} min${minutes > 1 ? 's' : ''}`;
                message = 'will join shortly';
            } else if (minutesDifference >= -5) {
                estimatedTime = '2 mins';
                message = 'should join any moment';
            } else if (minutesDifference >= -30) {
                // Appointment was 6-30 minutes ago
                estimatedTime = '5 mins';
                message = 'will join shortly';
            } else if (minutesDifference >= -120) {
                // Appointment was 30 minutes to 2 hours ago
                estimatedTime = '15 mins';
                message = 'may be running late - will join soon';
            } else {
                // Appointment was more than 24 hours ago
                estimatedTime = ' ';
                message = 'appointment may be in the past or need rescheduling. Please contact Ankr Support at 888-488-ANKR.';
            }
            
            return {
                estimatedTime,
                providerName: formattedAppointmentInfo.providerName || 'Your provider',
                message
            };
        } catch (error) {
            console.error('Error calculating wait time:', error);
            return null; // Return null on error instead of fallback values
        }
    }, [formattedAppointmentInfo]);

    // Notify backend of check-in completion
    useEffect(() => {
        const notifyBackend = async () => {
            if (hasNotifiedBackend.current || !shouldNotifyBackend.current || !tokenData?.patient_id) return;

            shouldNotifyBackend.current = false;

            try {
                await patientApi.completeCheckIn(
                    tokenData.patient_id,
                    { ...checkInData, skippedForms },
                    tokenData
                );
                hasNotifiedBackend.current = true;
            } catch (error) {
                console.error('Error notifying backend:', error);
            }
        };

        notifyBackend();
    }, [tokenData, checkInData, skippedForms]);

    return (
        <div className="container py-4">
            {/* Responsive container with proper padding on mobile */}
            <style>{`
                @media (max-width: 576px) {
                    .container {
                        padding-left: 15px;
                        padding-right: 15px;
                    }
                }
            `}</style>

            {/* Mobile Wellness Games Page */}
            {showWellnessGames ? (
                <WellnessGamesPage onBack={() => setShowWellnessGames(false)} />
            ) : (
                <>
                    <div className="text-center mb-5">
                        <div className="mb-4" style={{ display: 'flex', justifyContent: 'center' }}>
                            <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect width="80" height="80" rx="40" fill="#DFF2D9" />
                                <path d="M50.9664 30.8975C51.569 31.5604 51.569 32.6368 50.9664 33.2997L38.6249 46.8747C38.0223 47.5375 37.0437 47.5375 36.4411 46.8747L30.2703 40.0872C29.6677 39.4243 29.6677 38.3479 30.2703 37.685C30.8729 37.0222 31.8516 37.0222 32.4542 37.685L37.5354 43.2688L48.7874 30.8975C49.39 30.2347 50.3686 30.2347 50.9712 30.8975H50.9664Z" fill="#4CA022" />
                            </svg>
                        </div>
                        
                        <p className="text-muted mb-4" style={{ fontSize: '1.1rem', color: '#333', fontWeight: '600', margin: '0 auto' }}>
                            You&apos;re all set{formattedAppointmentInfo ? (<><span> for your {formattedAppointmentInfo.appointmentType} appointment!</span><p style={{marginTop: '10px',fontWeight: '400'}}>{formattedAppointmentInfo.formattedDate} at {formattedAppointmentInfo.formattedTime} {formattedAppointmentInfo.timezone}</p></>):(<span>!</span>)}
                        </p>
                        <p className="text-muted mb-4" style={{ fontSize: '1.1rem', margin: '0 auto' }}>
                            We will text & email you once provider has joined. Keep your ringer on and be ready.
                        </p>
                    </div>

                    {/* Zoom Join handled by ZoomMeeting component below */}

                    {/* Before your visit alert - Responsive styling */}
                    {/* <div className="bg-warning-subtle text-center rounded p-3 m-3">
                        <div style={{fontSize:'1.1em'}}><i className="fas fa-exclamation-triangle me-2"></i>Before your visit</div>
                        <div style={{ color: '#664D03' }}>Make sure you&apos;re in a quiet space with good lighting and stable internet connection.</div>
                    </div> */}

                    {/* Provider Card */}
                    <ProviderCard waitTimeInfo={waitTimeInfo} />

                    {/* Wellness Activities */}
                    <div className="row mb-4">
                        {/* Stay Relaxed */}
                        <div className="col-12 col-md-6 mb-4 mb-md-0">
                            <StayRelaxed />
                        </div>

                        {/* Wellness Games - Collapsible on mobile */}
                        <div className="col-12 col-md-6 d-flex">
                            {/* Mobile Collapsible Version */}
                            <div className="d-md-none w-100">
                                <div className="card h-100" style={{ borderRadius: '10px', border: '1px dashed #ccc', backgroundColor: '#f8f9fe' }}>
                                    <div
                                        className="d-flex align-items-center p-3"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => setShowWellnessGames(true)}
                                    >
                                        <div className="me-3">
                                            <div style={{
                                                width: '48px',
                                                height: '48px',
                                                borderRadius: '50%',
                                                backgroundColor: '#e8eaff',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                <i className="fas fa-gamepad" style={{ color: '#5f63f2', fontSize: '20px' }}></i>
                                            </div>
                                        </div>
                                        <div className="flex-grow-1">
                                            <h6 className="mb-1">Play a Wellness Game</h6>
                                            <p className="text-muted mb-0" style={{ fontSize: '14px' }}>Pass time with mindful activities</p>
                                        </div>
                                        <div>
                                            <i className="fas fa-chevron-right text-muted"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Desktop Full Version */}
                            <div className="d-none d-md-block w-100">
                                <div className="card h-100" style={{ backgroundColor: '#f8f9fe', borderRadius: '10px', border: 'none' }}>
                                    <div className="card-body p-4">
                                        <h5 className="mb-3">Wellness Games</h5>
                                        <p className="text-muted mb-3">Pass the time with these quick activities</p>

                                        {/* Games container with responsive layout */}
                                        <div className="row">
                                            <div className="col-12 col-sm-6 mb-4 mb-sm-0">
                                                <TicTacToe />
                                            </div>
                                            <div className="col-12 col-sm-6">
                                                <MemoryMatch />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Wellness & Learning Section */}
                    <div className="mb-5">
                        <h4 className="mb-4" style={{ color: '#333', fontWeight: '600' }}>Wellness & Learning</h4>
                        <div className="row">
                            {/* Health Tip Card - Full width on mobile, 1/3 on larger screens */}
                            <div className="col-12 col-sm-6 col-md-4 mb-4">
                                <div className="card h-100" style={{ borderRadius: '10px', border: 'none', backgroundColor: '#f8f9fe' }}>
                                    <div className="card-body p-4">
                                        <div className="text-center mb-3">
                                            <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: '#e6e7ff', display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '0 auto' }}>
                                                <i className="fas fa-lightbulb" style={{ color: '#5f63f2' }}></i>
                                            </div>
                                        </div>
                                        <h5 className="card-title text-center mb-3">Health Tip of the Week</h5>
                                        <p className="card-text text-muted">Regular monitoring of your blood pressure can help identify patterns and triggers.</p>
                                        <div className="text-center mt-3">
                                            <a href="#" className="text-primary" style={{ color: '#5f63f2', fontWeight: '500' }}>Read More</a>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Blood Pressure Video Card - Full width on mobile, 1/3 on larger screens */}
                            <div className="col-12 col-sm-6 col-md-4 mb-4">
                                <div className="card h-100" style={{ borderRadius: '10px', border: 'none', backgroundColor: '#f8f9fe' }}>
                                    <div className="card-body p-4">
                                        <div className="text-center mb-3">
                                            <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: '#e6e7ff', display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '0 auto' }}>
                                                <i className="fas fa-play" style={{ color: '#5f63f2' }}></i>
                                            </div>
                                        </div>
                                        <h5 className="card-title text-center mb-3">Understanding your blood pressure meds</h5>
                                        <p className="card-text text-muted">Learn about how your medication works and why consistency matters.</p>
                                        <div className="text-center mt-3">
                                            <a href="#" className="text-primary" style={{ color: '#5f63f2', fontWeight: '500' }}>Watch Video</a>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Anxiety Tips Card - Full width on mobile, 1/3 on larger screens */}
                            <div className="col-12 col-sm-6 col-md-4 mb-4">
                                <div className="card h-100" style={{ borderRadius: '10px', border: 'none', backgroundColor: '#f8f9fe' }}>
                                    <div className="card-body p-4">
                                        <div className="text-center mb-3">
                                            <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: '#e6e7ff', display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '0 auto' }}>
                                                <i className="fas fa-book" style={{ color: '#5f63f2' }}></i>
                                            </div>
                                        </div>
                                        <h5 className="card-title text-center mb-3">5 ways to reduce anxiety before a virtual visit</h5>
                                        <p className="card-text text-muted">Simple techniques to help you feel more comfortable during tele-health appointments.</p>
                                        <div className="text-center mt-3">
                                            <a href="#" className="text-primary" style={{ color: '#5f63f2', fontWeight: '500' }}>Read More</a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Before your visit alert - Responsive styling */}
                    <div className="alert" style={{ backgroundColor: '#FFF3E0', borderRadius: '10px', padding: '16px', marginBottom: '24px', display: 'flex', alignItems: 'flex-start', border: 'none', boxShadow: '0 2px 8px rgba(255, 152, 0, 0.1)', flexWrap: 'wrap' }}>
                        <div style={{ marginRight: '12px', color: '#FF9800' }}>
                            <i className="fas fa-exclamation-triangle" style={{ fontSize: '20px' }}></i>
                        </div>
                        <div>
                            <div style={{ fontWeight: '600', color: '#FF9800', marginBottom: '4px' }}>Before your visit</div>
                            <div style={{ color: '#664D03' }}>Make sure you&apos;re in a quiet space with good lighting and stable internet connection.</div>
                        </div>
                    </div>

                    {/* Zoom Meeting Component */}
                    <Suspense fallback={<div>Loading...</div>}>
                        <ZoomMeeting meetingInfo={zoomMeetingInfo} />
                    </Suspense>
                </>
            )}
        </div>
    );
};

export default CheckinComplete;
